/**
 * Las fichas que acompañan al estado de un caso en cualquier lista.
 *
 * Qué significa cada una lo explica la Ayuda de cada lista
 * (`components/ayuda/vistas/fichasCaso.tsx`), no un tooltip: si agregas o cambias una
 * ficha aquí, actualiza también esa explicación.
 *
 * Vivían dentro de `pages/Casos.tsx`. Se sacaron cuando el tablero «El proveedor
 * contestó» tuvo que pintar las mismas: son el vocabulario visual de un caso, y
 * un caso se ve igual en la lista donde esté. Copiarlas habría dejado dos
 * verdades del mismo dato.
 */

/**
 * «Alguien te mencionó aquí y todavía no lo resuelves».
 *
 * Dos tonos, no dos fichas: ÁMBAR cuando ni la has abierto, GRIS cuando ya la
 * viste pero sigue sin responder. Marcarla leída no la resuelve —sigues
 * debiendo la respuesta— así que la ficha no desaparece; solo deja de gritar.
 * Es la misma regla que ya usaba el contador de la campana, que sí excluía las
 * leídas mientras esta ficha las pintaba igual de fuerte.
 */
export function EsperanBadge({ n, sinLeer }: { n: number; sinLeer?: number }) {
  if (!n) return null;
  // `sinLeer` no viene en los listados viejos: sin el dato, se asume sin leer.
  const nuevas = sinLeer ?? n;
  return (
    <span
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
        nuevas > 0
          ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
          : "border-ink-600 bg-ink-800/60 text-zinc-400"
      }`}
    >
      ⏳ te esperan
    </span>
  );
}

// Badge de posesión: solo cuando la pelota NO la tenemos nosotros (cliente o
// proveedor) — son los casos que conviene vigilar. "Nosotros" no pinta nada
// (es el estado normal y el pill de estado ya lo dice). Deriva del estado.
export function PosesionBadge({ posesion }: { posesion: string | null }) {
  if (posesion === "cliente")
    return (
      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300">
        ⏳ Cliente
      </span>
    );
  if (posesion === "proveedor")
    return (
      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-orange-500/30 bg-orange-500/10 text-orange-300">
        📦 Proveedor
      </span>
    );
  return null;
}

/**
 * «El proveedor contestó y falta la nuestra».
 *
 * Se lee como un estado pero NO es un estado, a propósito: sale de
 * `casos.proveedor_respondio_at`, que convive con el estado sin pisarlo. Un
 * estado guarda un solo valor, así que si el cliente nos debe fotos Y el
 * proveedor contestó, uno de los dos se perdería.
 */
export function ProveedorBadge({ desde }: { desde: string | null | undefined }) {
  if (!desde) return null;
  const dias = diasDesde(desde);
  const tono =
    dias >= 5
      ? "border-red-500/40 bg-red-500/10 text-red-300"
      : dias >= 2
        ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
        : "border-accent-500/40 bg-accent-500/10 text-accent-300";
  return (
    <span
      // Corto y `whitespace-nowrap`: la columna Estado es angosta y la etiqueta
      // larga se partía en cuatro renglones, uno por palabra. Lo que significa
      // lo explica la Ayuda de la lista.
      className={`text-[10px] whitespace-nowrap px-1.5 py-0.5 rounded border ${tono}`}
    >
      {/* "Ya contestó" y no "Proveedor": en una fila con estado «Esperando
          proveedor» y la ficha de posesión 📦 PROVEEDOR, la palabra ya aparece
          dos veces y una tercera no distingue nada. El verbo sí. */}
      ⏳ Ya contestó{dias > 0 ? ` · ${dias} d` : ""}
    </span>
  );
}

// Badge rojo cuando el caso tiene ≥1 IMEI marcado CRÍTICO (roll-up).
export function CriticoBadge({ on }: { on: boolean }) {
  if (!on) return null;
  return (
    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-300">
      ⚠ crítico
    </span>
  );
}

/**
 * Ficha del ticket del proveedor. Con `estadoJimi`/`estadoQueclink` muestra
 * además el estado del ticket allá, A LA VISTA: antes iba en un tooltip junto
 * con el número, invisible en el celular. Las listas angostas (Casos) no lo
 * pasan; el número y el estado están en el encabezado del caso.
 */
export function TicketProveedorBadge({
  jimi,
  queclink,
  estadoJimi,
  estadoQueclink,
}: {
  jimi?: string | null;
  queclink?: number | null;
  /** Estado del ticket allá, ya en texto. */
  estadoJimi?: string | null;
  estadoQueclink?: string | null;
}) {
  if (jimi)
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded border border-teal-500/30 bg-teal-500/10 text-teal-300 font-mono">
        🎫 Jimi{estadoJimi && <span className="font-sans"> · {estadoJimi}</span>}
      </span>
    );
  if (queclink)
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300 font-mono">
        🎫 Queclink{estadoQueclink && <span className="font-sans"> · {estadoQueclink}</span>}
      </span>
    );
  return null;
}

/** Días completos transcurridos desde una fecha ISO. Nunca negativo. */
export function diasDesde(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}
